import {
  Video, BarChart3, FileText, Zap, Camera, Upload, Lock, Play, MessageSquare, Tags, Layers,
  Users, Download, ClipboardCheck, ListChecks, Timer, MapPin, PieChart, LineChart, Eye, Briefcase,
  Radar, Target, Filter,
} from "lucide-react";
import type { DetailContent } from "./types";

export const platform: DetailContent[] = [
  {
    slug: "video-proof",
    family: "platform",
    icon: Video,
    name: "Video Proof",
    keyword: "video proof of work hiring",
    summary: "Short clips that show real skill instead of a CV.",
    metaTitle: "Video Proof of Work Hiring | Donjo Platform",
    metaDescription:
      "Video proof of work hiring: applicants record short clips that show real skill. Use them for jobs, challenges and pitches. Start free on Donjo.",
    eyebrow: "Platform",
    headline: "A CV says it. A proof clip shows it.",
    subhead: "Video proof of work hiring: applicants demonstrate real skill in a short clip, and reviewers watch it in one place.",
    glance: {
      title: "Used for",
      items: ["Job application answers", "Challenge submissions", "Pitch videos and portfolios"],
    },
    problem: {
      title: "Written claims are cheap",
      points: ["A CV can't be checked at a glance", "Pedigree becomes a proxy for skill", "Communication only shows in interviews"],
    },
    help: {
      title: "Evidence in the first minute",
      intro: "One prompt, many clips, watched back to back and compared fairly.",
      pillars: [
        { title: "Record or upload", body: "Camera in the browser, or a file." },
        { title: "Made for a question", body: "Prompts keep clips focused." },
        { title: "Owned by applicants", body: "They choose public or private." },
      ],
    },
    capabilitiesTitle: "What's included",
    capabilities: [
      { icon: Camera, title: "In-browser recording", body: "No install needed." },
      { icon: Upload, title: "Video upload", body: "Bring a clip you already made." },
      { icon: Target, title: "Video prompts", body: "Attach a question to jobs and challenges." },
      { icon: Tags, title: "Skill categories", body: "Group clips by skill shown." },
      { icon: Lock, title: "Public or private", body: "Applicants control visibility." },
      { icon: MessageSquare, title: "Messaging", body: "Employers can message candidates they shortlist." },
    ],
    workflowTitle: "Life of a proof clip",
    workflow: [
      { title: "Prompt", body: "A team sets the question." },
      { title: "Record", body: "The applicant captures a clip." },
      { title: "Submit", body: "It attaches to an application or profile." },
      { title: "Decide", body: "Reviewers watch and shortlist." },
    ],
    audienceTitle: "Who uses it",
    audience: [
      { title: "Applicants", body: "Skilled people without a polished CV." },
      { title: "Hiring teams", body: "Judge substance before scheduling." },
      { title: "Organisers", body: "Entries in one comparable format." },
    ],
    faq: [
      { q: "How long should a clip be?", a: "Designed around about a minute. The in-browser recorder stops at two minutes, and you can upload a file of up to 100 MB instead." },
      { q: "Is video scored automatically?", a: "No. Nothing is scored by software. People watch and decide." },
      { q: "Who can watch my videos?", a: "Public videos appear on your profile. Private videos are visible only to you, apart from one exception: if you submit a private video to an employer's challenge, that employer can watch it." },
      { q: "Do I have to record on the spot?", a: "No. You can record in your browser or upload a clip you already made." },
      { q: "What do employers see when I apply?", a: "Your cover message, your profile and skills, and your most recent public videos, up to six." },
      { q: "Can a job ask a specific question?", a: "Yes. A job or challenge can carry a video question, and applicants see it before they record." },
      { q: "Can I delete a video?", a: "You can delete your account and data from your account settings. Deleting individual videos from the app is not offered on its own." },
    ],
    related: ["/platform/skill-radar", "/platform/dossier-generation", "/solutions/hr-for-startups"],
  },

  {
    slug: "skill-radar",
    family: "platform",
    icon: BarChart3,
    name: "Skill Radar",
    keyword: "applicant skills assessment",
    summary: "Rate an applicant on five areas and see their strengths at a glance.",
    metaTitle: "Skill Radar: Rate Applicants on Five Areas | Donjo",
    metaDescription:
      "Structured applicant ratings for hiring: reviewers score communication, technical skill, problem solving, role fit and presentation, and Donjo draws the radar. Try Donjo.",
    eyebrow: "Platform",
    headline: "See an applicant's strengths at a glance.",
    subhead: "Reviewers rate five areas from 1 to 5 and Donjo draws the radar. Skills on the profile are matched against what the job requires.",
    glance: {
      title: "Available",
      items: ["Skills on every profile", "Skill match against the job", "Five-area reviewer ratings"],
    },
    problem: {
      title: "Shortlist or reject is not enough",
      points: ["Free-text skills can't be compared", "Reviewers describe strengths in different ways", "Impressions get lost between meetings"],
    },
    help: {
      title: "Consistent ratings, one readable chart",
      intro: "Every rating uses the same five areas and the same 1 to 5 scale.",
      pillars: [
        { title: "Structured", body: "Communication, technical skill, problem solving, role fit and presentation." },
        { title: "Matched", body: "Profile skills are compared with the job's required skills." },
        { title: "Human-led", body: "The radar shows your own ratings. People decide." },
      ],
    },
    capabilitiesTitle: "What sits behind it",
    capabilities: [
      { icon: Tags, title: "Profile skills", body: "A skills list plus video categories." },
      { icon: Layers, title: "Industry and tech tags", body: "Captured on venture applications." },
      { icon: Target, title: "Skill match", body: "Required skills on the job against skills on the profile." },
      { icon: ClipboardCheck, title: "Reviewer assessments", body: "Five ratings and a private note beyond shortlist or reject." },
      { icon: Radar, title: "Per-applicant radar", body: "Your ratings drawn as a chart in the applicant's dossier." },
      { icon: Filter, title: "Browse by category", body: "Find relevant proof fast." },
    ],
    workflowTitle: "How the picture is built",
    workflow: [
      { title: "Describe", body: "Applicants add skills to their profile." },
      { title: "Match", body: "Their skills are compared with the job." },
      { title: "Rate", body: "You score the five areas after watching their proof." },
      { title: "Read", body: "The radar shows strengths and gaps." },
    ],
    audienceTitle: "Who it helps",
    audience: [
      { title: "Hiring teams", body: "Comparing applicants on the same footing." },
      { title: "Reviewers", body: "Recording a view without a long write-up." },
      { title: "Applicants", body: "Being assessed on the work they show." },
    ],
    faq: [
      { q: "Is there a radar for each applicant?", a: "Yes. Open an applicant from your job's applicant list, choose Dossier, and rate the five areas. The radar draws from your ratings." },
      { q: "What are the five areas?", a: "Communication, technical skill, problem solving, role fit and presentation. Each is rated from 1 (weak) to 5 (excellent), and all five are needed to save." },
      { q: "Does Donjo score applicants automatically?", a: "No. Nothing is scored by software. The radar only shows the ratings you enter." },
      { q: "Who can see my ratings and notes?", a: "They belong to the employer account that posted the job. Applicants do not see them." },
      { q: "How does the skill match work?", a: "It compares the skills listed on the applicant's profile with the skills you marked as required on the job, by name. It shows what matches and what isn't listed. It does not judge ability." },
      { q: "Are skills verified?", a: "No. Skills on a profile are self-reported, which is why the applicant's video proof sits beside them." },
      { q: "Can I change a rating later?", a: "Yes. Saving again replaces your earlier ratings and note for that applicant." },
    ],
    related: ["/platform/video-proof", "/platform/venture-velocity", "/platform/dossier-generation"],
  },

  {
    slug: "dossier-generation",
    family: "platform",
    icon: FileText,
    name: "Dossier",
    keyword: "applicant dossier",
    summary: "Everything about one applicant in one place, plus a PDF or CSV summary.",
    metaTitle: "Applicant Dossier and PDF Export | Donjo Platform",
    metaDescription:
      "Open a full applicant dossier on screen, with skills, cover message, videos, ratings and status history, or export a PDF or CSV summary for panels. Start free on Donjo.",
    eyebrow: "Platform",
    headline: "Everything about an applicant, in one place.",
    subhead: "Open a dossier on screen, or export a PDF or CSV summary for panels and advisors.",
    glance: {
      title: "A dossier has",
      items: ["Skills, bio and cover message", "Your ratings, notes and radar", "Status history with dates"],
    },
    problem: {
      title: "Decisions happen in meetings",
      points: ["Applicant details are scattered across tools", "Video links get lost", "Advisors without logins need a summary"],
    },
    help: {
      title: "From queue to committee pack",
      intro: "Built from live records, linked to the evidence.",
      pillars: [
        { title: "On screen", body: "Open any applicant's dossier from the applicant list." },
        { title: "Linked", body: "Their video portfolio is one tap away." },
        { title: "Exportable", body: "A PDF for the room, or CSV for a spreadsheet." },
      ],
    },
    capabilitiesTitle: "What you get",
    capabilities: [
      { icon: Briefcase, title: "On-screen dossier", body: "Bio, location, skills and cover message together." },
      { icon: Target, title: "Skill match", body: "Their skills against the job's required skills." },
      { icon: Radar, title: "Ratings and notes", body: "Five-area ratings, radar and a private note." },
      { icon: ListChecks, title: "Status history", body: "Every shortlist, review and reject, with the time." },
      { icon: Download, title: "PDF export", body: "An A4 table of names, roles and video links, built in your browser." },
      { icon: FileText, title: "CSV fallback", body: "Same data for spreadsheets." },
    ],
    workflowTitle: "Using a dossier",
    workflow: [
      { title: "Collect", body: "Applications arrive and are reviewed." },
      { title: "Open", body: "Read the dossier and rate the applicant." },
      { title: "Export", body: "Download the PDF or CSV when you need one." },
      { title: "Follow", body: "Readers click through to the videos." },
    ],
    audienceTitle: "Who uses it",
    audience: [
      { title: "Hiring teams", body: "One view of each applicant." },
      { title: "Selection panels", body: "One document for the room." },
      { title: "Programme managers", body: "A snapshot to archive." },
    ],
    faq: [
      { q: "What is in the on-screen dossier?", a: "The applicant's bio, location, skills and how they match the job, their cover message, their video portfolio, your ratings with the radar and your private note, and the status history with dates." },
      { q: "What is in the PDF?", a: "A summary table: applicant names, the role applied for and a link to each video portfolio. It does not include ratings or notes." },
      { q: "Who can open or export one?", a: "Employers, for their own jobs, from the job's applicant list. Donjo admins can export venture applications from the admin console." },
      { q: "Can I edit the PDF?", a: "No. Use the CSV version to edit in a spreadsheet." },
      { q: "Where does the status history come from?", a: "Each time an application is reviewed, shortlisted or rejected, Donjo records the new status and the time. Nothing is back-filled." },
      { q: "Is a generated PDF stored?", a: "No. It is built in your browser when you ask and is not kept by Donjo." },
    ],
    related: ["/platform/video-proof", "/platform/skill-radar", "/solutions/accelerators"],
  },

  {
    slug: "venture-velocity",
    family: "platform",
    icon: Zap,
    name: "Venture Velocity",
    keyword: "time to decision hiring",
    summary: "How fast applications are decided, and where applicants are.",
    metaTitle: "Time-to-Decision and Applicant Map | Venture Velocity | Donjo",
    metaDescription:
      "Hiring speed analytics: average and median time-to-decision from real status changes, plus a Kenya county map of where applicants are. See Donjo.",
    eyebrow: "Platform",
    headline: "Know how fast your decisions are made.",
    subhead: "Time-to-decision from the records Donjo already keeps, and a county map of where your applicants are.",
    glance: {
      title: "Covers",
      items: ["Time-to-decision", "Shortlisted and rejected counts", "County map of applicants"],
    },
    problem: {
      title: "Slow decisions cost good people",
      points: ["Nobody knows the real time-to-decision", "Applications wait unseen", "Outreach is guesswork without origin data"],
    },
    help: {
      title: "Measure what reviewers already do",
      intro: "Every application and decision is recorded in Donjo, so nothing is entered twice.",
      pillars: [
        { title: "Time-to-decision", body: "From the application to the first shortlist or reject." },
        { title: "By job", body: "See which roles wait longest." },
        { title: "Where from", body: "A county map of Kenya from what applicants entered." },
      ],
    },
    capabilitiesTitle: "What's in the analytics",
    capabilities: [
      { icon: Timer, title: "Time-to-decision", body: "Average, median, fastest and slowest, in days." },
      { icon: ClipboardCheck, title: "Waiting count", body: "Applications not yet decided, counted separately." },
      { icon: LineChart, title: "Decisions over time", body: "Decisions per week across the platform, in the admin console." },
      { icon: MapPin, title: "County applicant map", body: "Where your applicants are, by Kenyan county." },
      { icon: PieChart, title: "Review outcomes", body: "Shortlisted, rejected and waiting." },
      { icon: Eye, title: "Role-restricted", body: "Employers see their own jobs. Admins see the platform." },
    ],
    workflowTitle: "Using it in a round",
    workflow: [
      { title: "Open", body: "Go to your employer dashboard." },
      { title: "Read", body: "Check decision times, overall and by job." },
      { title: "Spot gaps", body: "See where applicants are, and where they are not." },
      { title: "Act", body: "Clear the queue and shorten the wait." },
    ],
    audienceTitle: "Who uses it",
    audience: [
      { title: "Hiring managers", body: "See where decisions stall." },
      { title: "HR teams", body: "Report one set of numbers." },
      { title: "Programme leads", body: "Understand reach across counties." },
    ],
    faq: [
      { q: "How is time-to-decision measured?", a: "From the moment an application arrives to the first time it is shortlisted or rejected, using the recorded timestamps. Applications still waiting are counted separately and never estimated." },
      { q: "What if I have no decisions yet?", a: "The dashboard says so. It shows no figure until there is a real decision to measure." },
      { q: "Where do the counties come from?", a: "From the county each applicant chose on their own profile. Applicants who did not set a location, or who are outside Kenya, are counted separately." },
      { q: "Is the map a precise location?", a: "No. It is a tile map of Kenya's 47 counties, coloured by how many applicants chose each county. It is not to scale." },
      { q: "Who sees these numbers?", a: "Employers see figures for their own jobs only. Donjo admins see platform-wide figures in the admin console." },
      { q: "Can I export analytics?", a: "You can export applicants as PDF or CSV. The analytics figures themselves are not exported." },
    ],
    related: ["/platform/skill-radar", "/platform/dossier-generation", "/solutions/accelerators"],
  },
];
